// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Observation, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  getAdherenceColor,
  getCalendarMonthBounds,
  isRpmSyncRequirementMet,
  isTestPatient,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  RPM_CARE_PLAN_CATEGORY_CODE,
  RPM_CARE_PLAN_CATEGORY_SYSTEM,
  RPM_OBSERVATION_CODES,
  RPM_OBSERVATION_SYSTEM,
  RPM_REQUIREMENT_THRESHOLDS,
  type RpmMonitoringStatus,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getRpmObservationCode,
  getRpmObservationDateTime,
  getRpmObservationNumber,
  getRpmObservationStatus,
  type RpmObservationCode,
} from '../utils/rpmFhir';

const ACTIVE_RPM_CARE_PLAN_LIMIT = 500;
const RPM_OBSERVATION_CODE_FILTER = Object.values(RPM_OBSERVATION_CODES)
  .map((code) => `${RPM_OBSERVATION_SYSTEM}|${code}`)
  .join(',');

export interface RemoteMonitoringEntry {
  patientId: string | undefined;
  patientName: string;
  triageStatusCode: string | undefined;
  assignedTo: string | undefined;
  lastUpdate: string | undefined;
  telecom: PatientResult['telecom'];
  address: PatientResult['address'];
  extension: PatientResult['extension'];
  adherenceNights: number | undefined;
  adherenceColor: 'green' | 'yellow' | 'red' | undefined;
  lastContactDate: string | undefined;
  asyncMinutes: number;
  syncMinutes: number;
  asyncComplete: boolean;
  syncComplete: boolean;
  monitoringStatus: RpmMonitoringStatus | undefined;
  rpmObservations: Partial<Record<RpmObservationCode, Observation>>;
  isTestPatient: boolean;
}

interface PatientResult {
  id?: string;
  name?: { given?: string[]; family?: string }[];
  meta?: { lastUpdated?: string; tag?: { system?: string; code?: string }[] };
  generalPractitioner?: { display?: string }[];
  telecom?: { system?: string; value?: string; use?: string; rank?: number }[];
  address?: { line?: string[]; city?: string; state?: string; postalCode?: string }[];
  extension?: {
    url?: string;
    valueString?: string;
    valueCode?: string;
    valueCodeableConcept?: { text?: string; coding?: { system?: string; code?: string; display?: string }[] };
  }[];
}

function getPatientIdFromObservation(observation: Observation): string | undefined {
  return observation.subject?.reference?.replace('Patient/', '');
}

function mapPatientToEntry(
  patient: PatientResult,
  observations: Partial<Record<RpmObservationCode, Observation>> = {}
): RemoteMonitoringEntry {
  const triageTag = patient.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM);
  const adherenceNights = getRpmObservationNumber(observations[RPM_OBSERVATION_CODES.adherenceNights30Day]);
  const asyncMinutes = getRpmObservationNumber(observations[RPM_OBSERVATION_CODES.asyncMonitoringMinutes]) ?? 0;
  const syncMinutes = getRpmObservationNumber(observations[RPM_OBSERVATION_CODES.syncContactMinutes]) ?? 0;
  return {
    patientId: patient.id,
    patientName: patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient',
    triageStatusCode: triageTag?.code,
    assignedTo: patient.generalPractitioner?.[0]?.display,
    lastUpdate: patient.meta?.lastUpdated,
    telecom: patient.telecom,
    address: patient.address,
    extension: patient.extension,
    adherenceNights,
    adherenceColor: adherenceNights !== undefined ? getAdherenceColor(adherenceNights) : undefined,
    lastContactDate: getRpmObservationDateTime(observations[RPM_OBSERVATION_CODES.lastContactDate]),
    asyncMinutes,
    syncMinutes,
    asyncComplete: asyncMinutes >= RPM_REQUIREMENT_THRESHOLDS.totalMinutes,
    syncComplete: isRpmSyncRequirementMet(syncMinutes),
    monitoringStatus: getRpmObservationStatus(observations[RPM_OBSERVATION_CODES.monitoringStatus]),
    rpmObservations: observations,
    isTestPatient: isTestPatient(patient as Patient | undefined),
  };
}

function patientMatchesSearch(patient: PatientResult, search: string | undefined): boolean {
  if (!search?.trim()) {
    return true;
  }
  const normalizedSearch = search.trim().toLowerCase();
  return (
    patient.name?.some((name) =>
      [name.family, ...(name.given ?? [])].some((part) => part?.toLowerCase().includes(normalizedSearch))
    ) ?? false
  );
}

export function useRemoteMonitoringQueue({
  page,
  pageSize,
  sort,
  search,
  hideTestPatients,
}: {
  page: number;
  pageSize: number;
  sort?: TriageSortState;
  search?: string;
  hideTestPatients?: boolean;
}): { entries: RemoteMonitoringEntry[]; loading: boolean; total: number; refetch: () => void } {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<RemoteMonitoringEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const sortKey = useMemo(() => (sort ? `${sort.column}|${sort.direction}` : null), [sort]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sort tracked via sortKey (stable primitive)
  const fetchEntries = useCallback(async () => {
    setLoading(true);

    try {
      const activeRpmCarePlans = await medplum.searchResources('CarePlan', {
        status: 'active',
        category: `${RPM_CARE_PLAN_CATEGORY_SYSTEM}|${RPM_CARE_PLAN_CATEGORY_CODE}`,
        _count: String(ACTIVE_RPM_CARE_PLAN_LIMIT),
        _sort: '-_lastUpdated',
      });
      const activeRpmPatientIds = Array.from(
        new Set(
          activeRpmCarePlans
            .filter((carePlan) => carePlan.status === 'active')
            .map((carePlan) => carePlan.subject?.reference?.replace('Patient/', ''))
            .filter((id): id is string => Boolean(id))
        )
      );
      const uniquePatients =
        activeRpmPatientIds.length > 0
          ? (
              (await medplum.searchResources('Patient', {
                _id: activeRpmPatientIds.join(','),
                _count: String(activeRpmPatientIds.length),
              })) as PatientResult[]
            ).filter((patient) => patientMatchesSearch(patient, search))
          : [];
      uniquePatients.sort((a, b) => {
        if (sort?.column === 'name') {
          const aName = a.name?.[0] ? formatHumanName(a.name[0]) : '';
          const bName = b.name?.[0] ? formatHumanName(b.name[0]) : '';
          return (sort.direction === 'desc' ? -1 : 1) * aName.localeCompare(bName);
        }
        return (b.meta?.lastUpdated ?? '').localeCompare(a.meta?.lastUpdated ?? '');
      });
      const listForQueue = uniquePatients;
      const observationsByPatient = new Map<string, Partial<Record<RpmObservationCode, Observation>>>();

      const patientIds = listForQueue.map((patient) => patient.id).filter((id): id is string => Boolean(id));
      if (patientIds.length > 0) {
        const { start, end } = getCalendarMonthBounds(new Date());
        const params = new URLSearchParams();
        params.set('subject', patientIds.map((id) => `Patient/${id}`).join(','));
        params.set('code', RPM_OBSERVATION_CODE_FILTER);
        params.append('date', `ge${start.toISOString()}`);
        params.append('date', `lt${end.toISOString()}`);
        params.set('_count', String(patientIds.length * Object.values(RPM_OBSERVATION_CODES).length));
        params.set('_sort', '-_lastUpdated');
        const observationBundle = await medplum.search('Observation', params);

        for (const bundleEntry of observationBundle.entry ?? []) {
          const observation = bundleEntry.resource as Observation | undefined;
          if (!observation) {
            continue;
          }
          const patientId = getPatientIdFromObservation(observation);
          const code = getRpmObservationCode(observation);
          if (!patientId || !code) {
            continue;
          }
          const patientObservations = observationsByPatient.get(patientId) ?? {};
          if (!patientObservations[code]) {
            patientObservations[code] = observation;
            observationsByPatient.set(patientId, patientObservations);
          }
        }
      }

      const rows = listForQueue.map((patient) =>
        mapPatientToEntry(patient, patient.id ? observationsByPatient.get(patient.id) : undefined)
      );
      const visibleRows = hideTestPatients ? rows.filter((e) => !e.isTestPatient) : rows;
      const offset = (page - 1) * pageSize;
      setTotal(visibleRows.length);
      setEntries(visibleRows.slice(offset, offset + pageSize));
    } finally {
      setLoading(false);
    }
  }, [medplum, page, pageSize, sortKey, search, hideTestPatients]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, total, refetch: fetchEntries };
}
